import { useNavigate } from 'react-router-dom';
import { Box, Typography, Breadcrumbs, Link, Alert } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { trpc } from '../../lib/trpc';
import { ChannelForm } from '../../components/notifications';
import type { ChannelSubmitData } from '../../components/notifications';

/**
 * Add Channel page component
 * Allows users to create a new notification channel
 */
export function AddChannelPage() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  // Fetch limit info
  const { data: limitInfo } = trpc.notifications.limits.useQuery();

  // Create mutation
  const createMutation = trpc.notifications.create.useMutation({
    onSuccess: () => {
      // Invalidate queries to refresh data
      utils.notifications.list.invalidate();
      utils.notifications.limits.invalidate();

      navigate('/notifications');
    },
  });

  const handleSubmit = (data: ChannelSubmitData) => {
    createMutation.mutate({
      name: data.name,
      type: data.type,
      config: data.config,
    });
  };

  const handleCancel = () => {
    navigate('/notifications');
  };

  // Check if can add more channels
  const canAddChannel = limitInfo ? limitInfo.current < limitInfo.limit : true;

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/notifications" color="inherit" underline="hover">
          Notification Channels
        </Link>
        <Typography color="text.primary">Add Channel</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
          Add Notification Channel
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Configure a new channel to receive alerts about your monitors
        </Typography>
      </Box>

      {/* Limit warning */}
      {!canAddChannel && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You've reached your plan limit of {limitInfo?.limit} notification channels. Please upgrade
          your plan or delete an existing channel to add a new one.
        </Alert>
      )}

      {/* Form */}
      <ChannelForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={createMutation.isLoading}
        error={createMutation.error?.message ?? null}
      />
    </Box>
  );
}

export default AddChannelPage;
